import React from 'react';
import { Quote, Calendar, MapPin, Heart, ArrowRight } from 'lucide-react';
import { client, urlFor, generateSrcSet, urlForOptimized } from '../lib/sanity';
import type { SuccessStory } from '../types/sanity';
import { StoryDate } from './common/StoryDate';
import { LoadingMessage } from './common/LoadingMessage';
import { ErrorMessage } from './common/ErrorMessage';
import { useNavigate } from 'react-router-dom';

export function SuccessStories() {
  const [stories, setStories] = React.useState<SuccessStory[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 4;
  const navigate = useNavigate();

  const paginatedStories = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return stories.slice(startIndex, startIndex + itemsPerPage);
  }, [stories, currentPage]);

  const totalPages = Math.ceil(stories.length / itemsPerPage);

  React.useEffect(() => {
    const fetchStories = async () => {
      try {
        const result = await client.fetch<SuccessStory[]>(`
          *[_type == "successStory" && adoptionType == "direct" && isPublished == true && !(_id in path("drafts.**"))] | order(adoptionDate desc)[0...4] {
            _id,
            ownerName,
            adoptionDate,
            location,
            quote,
            testimony,
            impactQuote,
            image,
            media,
            wasSpecialCase,
            "pet": pet->{
              name,
              mainCategory,
              image
            },
            "additionalPets": additionalPets[]->{
              name,
              mainCategory,
              image
            }
          }
        `);
        setStories(result);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching success stories:', err);
        setError('Nie udało się załadować historii adopcyjnych');
        setLoading(false);
      }
    };

    fetchStories();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-accessible-gray-50 dark:bg-accessible-gray-900 high-contrast:bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <LoadingMessage ariaLabel="Ładowanie historii adopcyjnych" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-accessible-gray-50 dark:bg-accessible-gray-900 high-contrast:bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <ErrorMessage message={error} ariaLabel="Błąd ładowania historii adopcyjnych" />
        </div>
      </section>
    );
  }

  if (stories.length === 0) {
    return null;
  }

  return (
    <section className="py-16 px-12 bg-accessible-gray-50 dark:bg-accessible-gray-900 high-contrast:bg-black">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="section-title">
            Historie adopcyjne
          </h2>
          <p className="body-large">
            Zobacz, jak nasze zwierzęta znalazły szczęście w nowych domach
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {paginatedStories.map((story) => (
            <div 
              key={story._id} 
              className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black rounded-lg shadow-lg overflow-hidden cursor-pointer transform hover:scale-[1.02] transition-transform duration-300"
              onClick={() => navigate(`/historia/${story._id}`)}
              title={`Zobacz historię adopcyjną ${story.pet?.name}${story.additionalPets && story.additionalPets.length > 0 ? ` i ${story.additionalPets.length} ${story.additionalPets.length === 1 ? 'innego zwierzaka' : 'innych zwierzaków'}` : ''}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/historia/${story._id}`);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="relative">
                <div className="aspect-video">
                  <img
                    src={story.image ? urlForOptimized(story.image, 800, 450, 80) : 
                         story.pet?.image ? urlForOptimized(story.pet.image, 800, 450, 80) :
                         '/images/animals/fundacja-znajdki-zwierzeta-016.webp'}
                    srcSet={story.image ? generateSrcSet(story.image) : 
                           story.pet?.image ? generateSrcSet(story.pet.image) : 
                           ''}
                    sizes="(max-width: 768px) 100vw, 50vw"
                    alt={`${story.pet?.name || 'Podopieczny'} w nowym domu z ${story.ownerName}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="450"
                  />
                </div>
                {story.wasSpecialCase && (
                  <div className="absolute top-4 right-4 bg-accessible-red-600 text-white px-3 py-1 rounded-full text-sm">
                    Wyjątkowy przypadek
                  </div>
                )}
              </div>

              <div className="p-8">
                <div className="flex items-center gap-2 mb-4">
                  <Heart className="w-5 h-5 text-accessible-red-600" aria-hidden="true" />
                  <span className="text-sm text-accessible-gray-700 dark:text-white/90 high-contrast:text-white">
                    Nowy dom: {story.ownerName}
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-4">
                  Historia {story.pet?.name}
                  <span className="card-subtitle text-accessible-gray-700 dark:text-white/90 high-contrast:text-white">
                    {story.additionalPets && story.additionalPets.length > 0 && (
                      <> i {story.additionalPets.map(pet => pet.name).join(', ')}</>
                    )}
                  </span>
                </h3>

                <Quote className="w-8 h-8 text-accessible-red-600 mb-4" aria-hidden="true" />
                <blockquote className="body-large italic mb-6">
                  {story.quote}
                </blockquote>

                {story.impactQuote && (
                  <p className="body-medium mb-6 italic">
                    "{story.impactQuote}"
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-4 body-small text-accessible-gray-700 dark:text-white/90 high-contrast:text-white no-margin">
                  <StoryDate date={new Date(story.adoptionDate).toLocaleDateString('pl-PL', {
                    year: 'numeric',
                    month: 'long'
                  })} />
                  
                  {story.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{story.location}</span>
                    </div>
                  )}
                </div>

                {story.media && story.media.length > 0 && (
                  <div className="mt-6 grid grid-cols-4 gap-2">
                    {story.media.slice(0, 4).map((media, index) => (
                      'asset' in media ? (
                        <img
                          key={index}
                          src={urlFor(media).width(200).height(200).url()}
                          alt={`Dodatkowe zdjęcie ${index + 1}`}
                          className="w-full h-20 object-cover rounded-lg"
                        />
                      ) : null
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {stories.length > itemsPerPage && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            className="mb-8"
          />
        )}

        <div className="text-center">
          <button
            onClick={() => navigate('/historie-adopcyjne')}
            className="inline-flex items-center gap-2 bg-accessible-red-600 text-white px-6 py-3 rounded-lg hover:bg-accessible-red-700 transition-colors btn-text-medium"
            aria-label="Zobacz wszystkie historie adopcyjne"
          >
            <span>Zobacz więcej historii</span>
            <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}